/** @jsx jsx */
import { css, jsx } from '@emotion/react';

import { compose } from 'recompose';

import { humanizeLearningTime } from 'js/utils/DateTimeUtils';

import { Typography2 } from '@coursera/cds-core';
import { SvgHonorsAssignment } from '@coursera/coursera-ui/svg';

import { CoachItemType } from 'bundles/coach-item/common/types';
import LearnerAppClientNavigationLink from 'bundles/course-v2/components/navigation/LearnerAppClientNavigationLink';
import useCoachItemType from 'bundles/item-widget/hooks/useCoachItemType';
import EffortText from 'bundles/learner-progress/components/item/EffortText';
import LockedTooltip from 'bundles/learner-progress/components/item/locking/LockedTooltip';
import NavItemIcon from 'bundles/learner-progress/components/item/nav/private/NavItemIcon';
import NavItemName from 'bundles/learner-progress/components/item/nav/private/NavItemName';
import type { Item } from 'bundles/learner-progress/types/Item';
import {
  getFormattedTitlePrefix,
  getIsAssignmentPartForSplitPeer,
  getIsLocked,
  getIsLockedPGASubmissionPage,
  getIsMentorGraded,
  getIsReviewPartForSplitPeer,
  isGradeVisible,
} from 'bundles/learner-progress/utils/Item';
import waitForStores from 'bundles/phoenix/lib/waitForStores';

import _t from 'i18n!nls/learner-progress';

import 'css!./__styles__/NavSingleItemDisplay';

type PropsFromCaller = {
  computedItem: Item;
  highlighted?: boolean;
  trackingName?: string;
  openInNewWindow?: boolean;
  iconSize?: number;
  showDistinctAvailabilityReviewDates?: boolean;
};

export type Props = PropsFromCaller & {
  isGuidedProject?: boolean;
};

const styles = {
  root: css`
    align-items: center;
    display: flex;
    flex-direction: row;
    justify-content: flex-start;
    padding: var(--cds-spacing-100) var(--cds-spacing-300);
    position: relative;

    &:hover {
      background: var(--cds-color-grey-25);
      border-left: 6px solid var(--cds-color-blue-700);
      padding: var(--cds-spacing-100) var(--cds-spacing-300) var(--cds-spacing-100) 18px;
    }
  `,

  selected: css`
    background-color: var(--cds-color-grey-25);
    border-left: 6px solid var(--cds-color-blue-700);
    padding: var(--cds-spacing-100) var(--cds-spacing-300) var(--cds-spacing-100) 18px;

    @media (forced-colors: active) {
      background-color: WindowText;
    }
  `,
};

export const NavSingleItemDisplay = ({
  computedItem,
  highlighted,
  trackingName = 'item_link',
  openInNewWindow = false,
  iconSize,
  isGuidedProject,
  showDistinctAvailabilityReviewDates,
}: Props) => {
  let linkDescription = '';

  if (computedItem.contentSummary.typeName === 'quiz' || computedItem.contentSummary.typeName === 'exam') {
    const { questionCount } = computedItem.contentSummary.definition;
    if (questionCount === 1) {
      linkDescription = _t('#{questionCount} question', { questionCount });
    } else {
      linkDescription = _t('#{questionCount} questions', { questionCount });
    }
  } else if (getIsAssignmentPartForSplitPeer(computedItem) || getIsMentorGraded(computedItem)) {
    if (computedItem.isPeerAssignmentFailed) {
      linkDescription = _t('Try Again');
    } else if (computedItem.isPeerAssignmentWaitingForEvaluation) {
      linkDescription = _t('Grading in progress');
    } else if (computedItem.isCompleted && !computedItem.isCombinedItemCompleted) {
      linkDescription = _t('You must review more classmates');
    }
  } else if (
    computedItem.contentSummary.typeName === 'staffGraded' ||
    computedItem.contentSummary.typeName === 'ungradedAssignment' ||
    computedItem.contentSummary.typeName === 'teammateReview'
  ) {
    if (computedItem.isSubmitted) {
      linkDescription = _t('Submitted');
    } else if (computedItem.isStarted) {
      linkDescription = _t('Started');
    }
  } else if (computedItem.contentSummary.typeName === 'gradedDiscussionPrompt') {
    if (computedItem.isDiscussionPromptGrading) {
      linkDescription = _t('Grading in progress');
    } else if (computedItem.isDiscussionPromptStarted) {
      linkDescription = _t('Discussion prompt started');
    } else if (computedItem.isDiscussionPromptOverdue) {
      linkDescription = _t('Overdue');
    }
  } else if (!getIsReviewPartForSplitPeer(computedItem)) {
    linkDescription =
      humanizeLearningTime(computedItem.timeCommitment) &&
      `${humanizeLearningTime(computedItem.timeCommitment)} estimated time to complete.`;
  }

  const getComputedItemStatus = () => {
    let status = '';

    if (computedItem.isFailed && !isGradeVisible(computedItem)) {
      status = _t('Failed');
    } else if (computedItem.isPassed && !isGradeVisible(computedItem)) {
      status = _t('Passed');
    } else if (computedItem.isCompleted) {
      status = _t('Completed');
    } else if (getIsLocked(computedItem)) {
      status = _t('Locked');
    } else if (computedItem.deadlineProgress === 'OVERDUE') {
      status = _t('Overdue');
    }
    return status;
  };

  const coachItemType = useCoachItemType(computedItem);
  const typeNameOverride = coachItemType === CoachItemType.SocraticDialogue ? 'dialogue' : undefined;

  const formattedTitlePrefix = getFormattedTitlePrefix(computedItem, isGuidedProject, undefined, coachItemType);
  const computedItemHonorsLabel = computedItem.isHonors ? _t(' - optional Honors Item') : '';
  const computedItemStatus = getComputedItemStatus();
  const highlightedLabel = highlighted ? _t('selected link, ') : '';
  const ariaLabel = [
    `${highlightedLabel}${formattedTitlePrefix}`,
    `${computedItem.name}${computedItemHonorsLabel}`,
    `${linkDescription}`,
    `${computedItemStatus}`,
    `${openInNewWindow ? _t('Opens in a new tab') : ''}`,
  ]
    .filter((entry) => !!entry)
    .join(', ');
  return (
    <LearnerAppClientNavigationLink
      css={[styles.root, highlighted && styles.selected]}
      className="nostyle"
      trackingName={trackingName}
      href={computedItem.resourcePath}
      data={{ itemId: computedItem.id }}
      ariaLabel={ariaLabel}
      {...(openInNewWindow
        ? {
            target: '_blank',
            rel: 'noopener noreferrer',
          }
        : {})}
      data-testid="learner-app-client-navigation-link"
    >
      <Typography2
        component="div"
        className="rc-NavSingleItemDisplay"
        css={css`
          display: flex;
          flex-direction: row;
        `}
      >
        {(getIsLocked(computedItem) || getIsLockedPGASubmissionPage(computedItem)) && (
          <LockedTooltip
            placement="top"
            computedItem={computedItem}
            showDistinctAvailabilityReviewDates={showDistinctAvailabilityReviewDates}
          />
        )}
        <NavItemIcon computedItem={computedItem} size={iconSize} typeNameOverride={typeNameOverride} />

        <div>
          <NavItemName computedItem={computedItem} isGuidedProject={isGuidedProject} coachItemType={coachItemType} />
          <EffortText computedItem={computedItem} />
        </div>
        {computedItem.isHonors && (
          <div className="rc-HonorsContentLabel">
            <SvgHonorsAssignment size={22} />
          </div>
        )}
      </Typography2>
    </LearnerAppClientNavigationLink>
  );
};

export default compose<PropsFromCaller, Props>(
  waitForStores(['CourseStore'], ({ CourseStore }, props) => {
    return {
      ...props,
      isGuidedProject: CourseStore.isGuidedProject(),
    };
  })
)(NavSingleItemDisplay);
